import { useEffect, useMemo, useState } from 'react';
import { resolverPeriodo, type SeleccionPeriodo } from '@/lib/llamadas/fechas';
import { kpisDia } from '@/lib/llamadas/presentacion';
import {
  clientesQueMasLlaman,
  agruparSerie,
  deltaLlamadas,
  derivaciones,
  desenlace,
  distribucionMotivos,
  granularidadPara,
  serieDiaria,
  type Granularidad,
} from '@/lib/cuadro/agregaciones';
import {
  SIN_FILTROS,
  aplicarFiltros,
  hayFiltrosActivos,
  opcionesDisponibles,
  type FiltrosCuadro as Filtros,
} from '@/lib/cuadro/filtros';
import { contarLlamadasRango, fetchLlamadasRango } from '@/services/llamadas';
import type { LlamadaFeedRow } from '@/types/llamadas';
import { BotonesExportar } from './BotonesExportar';
import { CabeceraImpresion } from './CabeceraImpresion';
import { ClientesQueMasLlaman } from './ClientesQueMasLlaman';
import { Derivaciones } from './Derivaciones';
import { DistribucionMotivos } from './DistribucionMotivos';
import { FiltrosCuadro } from './FiltrosCuadro';
import { GraficaBarras } from './GraficaBarras';
import { GraficaDonut } from './GraficaDonut';
import { KpisCuadro } from './KpisCuadro';
import { SelectorPeriodo } from './SelectorPeriodo';
import { TablaLlamadas } from './TablaLlamadas';

// Cuadro de mando (prototipo iter5, cuadro-de-mando.html): KPIs del periodo +
// 4 gráficas + tabla histórica con deep-link a Atención. Agregaciones 100% en
// cliente; sin realtime en v1 (se entra a mirar la foto).

function Panel({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-soft)] print:break-inside-avoid">
      <h2 className="m-0 mb-3 text-[13.5px] font-semibold text-[var(--navy)]">{titulo}</h2>
      {children}
    </div>
  );
}

// Por encima de este umbral el cuadro pregunta antes de descargar. Se compara
// contra la SUMA de los dos periodos que se traen —el actual y el previo del
// delta—, así que en la práctica son unas mil por periodo. Número declarado, no
// medido: se recalibra cuando haya tráfico real (P4 del change).
const UMBRAL_AVISO = 2000;

// El informe impreso (change 003): A4 apaisado, fondo blanco y los colores reales,
// que el navegador quita al imprimir y dejan las gráficas vacías. No va en
// styles.css porque `@page` no admite selector: en una hoja global, Atención
// también se imprimiría apaisada y con los colores forzados. Se pone al montar el
// cuadro y se quita al salir. La CSP del panel admite estilos en línea
// (`style-src 'unsafe-inline'`); si eso cambia, el e2e del informe lo nota.
const ESTILO_IMPRESION = `
@page { size: A4 landscape; margin: 12mm; }
@media print {
  html, body { background: var(--surface); }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;

function useEstiloImpresion() {
  useEffect(() => {
    const estilo = document.createElement('style');
    estilo.dataset.informe = 'cuadro';
    estilo.textContent = ESTILO_IMPRESION;
    document.head.append(estilo);
    return () => estilo.remove();
  }, []);
}

const TITULO_SERIE: Record<Granularidad, string> = {
  dia: 'Llamadas por día',
  semana: 'Llamadas por semana',
  mes: 'Llamadas por mes',
};

export function CuadroPage() {
  useEstiloImpresion();
  const [periodo, setPeriodo] = useState<SeleccionPeriodo>('7d');
  const [filtros, setFiltros] = useState<Filtros>(SIN_FILTROS);
  const [actual, setActual] = useState<LlamadaFeedRow[]>([]);
  const [previo, setPrevio] = useState<LlamadaFeedRow[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generacion, setGeneracion] = useState(0);
  // Cuántas filas tiene el periodo pedido cuando supera el umbral, o null si no
  // hay nada que confirmar. Se guarda para poder decir el número en el aviso.
  const [porConfirmar, setPorConfirmar] = useState<number | null>(null);
  const [forzar, setForzar] = useState(false);
  // El conteo previo falló: se ha cargado igualmente, pero sin poder comprobar
  // el tamaño del periodo. Se dice, no se disimula.
  const [conteoFallido, setConteoFallido] = useState(false);
  // El rango de las filas que hay en pantalla, guardado al cargarlas. No se puede
  // recalcular desde `periodo`: «Hoy» y «Últimos 7 días» dependen del día, y si la
  // página sigue abierta pasada la medianoche y vuelve a cargar («Reintentar»,
  // «Cargar de todos modos»), las filas son del día nuevo. Recalculado aparte, la
  // gráfica, el informe impreso y el nombre del fichero dirían el día viejo.
  const [rangoActual, setRangoActual] = useState(() => resolverPeriodo(periodo).actual);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setError(null);
    setPorConfirmar(null);
    setConteoFallido(false);
    const rangos = resolverPeriodo(periodo);

    // Se cuenta ANTES de traer: preguntar cuesta una petición sin filas, y
    // cortar a mitad de la paginación ya habría descargado las primeras mil.
    // Se cuentan LOS DOS periodos porque se descargan los dos: el previo
    // alimenta el delta del KPI. Contando solo el actual, el aviso anunciaba la
    // mitad de lo que iba a traerse (AD3-P2-3).
    Promise.all([
      contarLlamadasRango(rangos.actual.desde, rangos.actual.hasta),
      contarLlamadasRango(rangos.previo.desde, rangos.previo.hasta),
    ])
      .then(([a, p]) => a + p)
      // Un contador caído NO puede dejar el cuadro inservible: el conteo sirve
      // para avisar antes de descargar, no para ver los datos. Pero degradar a
      // "periodo pequeño" a secas es peor de lo que parece: el fallo típico de
      // un COUNT exacto es el timeout sobre un rango ENORME, justo el caso
      // donde el guardarraíl hace falta. Así que se sigue adelante (como manda
      // el plan) pero se deja constancia visible en vez de fingir que el
      // periodo era pequeño.
      //
      // Matiz honesto (AD2-P3-4): seguir adelante no garantiza que la descarga
      // funcione. `_paginarFeed` pide `count: 'exact'` en la misma petición de
      // la primera página, así que un COUNT que revienta por tamaño se lleva
      // por delante también la carga, y lo que se verá es el error de siempre.
      // El aviso sigue valiendo para el caso en que solo falle esta petición.
      .catch((e: unknown) => {
        if (vigente) setConteoFallido(true);
        console.error(JSON.stringify({ evt: 'contar_rango_degradado', message: String(e) }));
        return 0;
      })
      .then((n) => {
        if (!vigente) return null;
        if (n > UMBRAL_AVISO && !forzar) {
          setPorConfirmar(n);
          setCargando(false);
          return null;
        }
        return Promise.all([
          fetchLlamadasRango(rangos.actual.desde, rangos.actual.hasta),
          fetchLlamadasRango(rangos.previo.desde, rangos.previo.hasta),
        ]);
      })
      .then((r) => {
        if (!vigente || r === null) return;
        setActual(r[0]);
        setPrevio(r[1]);
        setRangoActual(rangos.actual);
        setCargando(false);
      })
      .catch((e: unknown) => {
        if (!vigente) return;
        setError(e instanceof Error ? e.message : String(e));
        setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [periodo, generacion, forzar]);

  // Las opciones salen SIEMPRE del conjunto sin filtrar del periodo.
  const opciones = useMemo(() => opcionesDisponibles(actual), [actual]);
  // El filtro se aplica también al periodo previo: si no, el delta compararía
  // un subconjunto contra el total y saldría un porcentaje engañoso.
  const actualF = useMemo(() => aplicarFiltros(actual, filtros), [actual, filtros]);
  const previoF = useMemo(() => aplicarFiltros(previo, filtros), [previo, filtros]);

  // Por encima de 90 días una barra por día es un peine ilegible, así que la
  // serie se agrupa por semana o por mes. Es lo que permite que el rango custom
  // llegue a tres años sin que la gráfica deje de decir nada.
  const granularidad = granularidadPara(rangoActual.dias.length);
  const serie = useMemo(
    () => agruparSerie(serieDiaria(actualF, rangoActual.dias), granularidad),
    [actualF, rangoActual.dias, granularidad]
  );

  const periodoVacio = actual.length === 0;
  const hayFiltros = hayFiltrosActivos(filtros);
  const diaDesde = rangoActual.dias[0] ?? '';
  const diaHasta = rangoActual.dias.at(-1) ?? '';
  const razonSocialCliente =
    opciones.clientes.find((c) => c.id === filtros.cliente)?.razonSocial ?? null;

  return (
    <>
      <div className="mb-4 flex items-center gap-3 print:hidden">
        <h2 className="m-0 text-[14px] font-semibold text-[var(--navy)]">Resumen del servicio</h2>
        <div className="flex-1" />
        <SelectorPeriodo
          seleccion={periodo}
          onCambiar={(p) => {
            setFiltros(SIN_FILTROS);
            setForzar(false);
            setPeriodo(p);
          }}
        />
      </div>

      {cargando && (
        // `--text-2` y no `--text-dim`: este texto va sobre el fondo de la página, sin
        // caja blanca debajo, y ahí el gris se queda en 4,39:1 —por debajo de AA—.
        // Donde `--text-dim` sí llega es sobre la superficie blanca de los paneles.
        <p className="py-8 text-center text-[12.5px] text-[var(--text-2)]">
          Cargando el cuadro de mando…
        </p>
      )}

      {!cargando && error && (
        <div data-testid="cuadro-error" className="py-8 text-center">
          {/* El mensaje, no solo "no pude": cuando la carga falla por tamaño
              del periodo, el texto dice qué hacer (acortarlo) y sin pintarlo el
              usuario se quedaba con un "Reintentar" que repetía el fallo. */}
          {/* Igual que el de «Cargando…»: sobre el fondo de la página, `--text-dim`
              no llega a AA (4,39:1). Y este es el texto que dice qué hacer. */}
          <p className="text-[12.5px] text-[var(--text-2)]">{error}</p>
          <button
            type="button"
            onClick={() => {
              // `forzar` vuelve a false: si el usuario había forzado un periodo
              // enorme y la carga reventó, reintentar con el forzado puesto
              // repite el mismo fallo sin decir nada nuevo.
              setForzar(false);
              setGeneracion((g) => g + 1);
            }}
            className="mt-3 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-3.5 py-1.5 text-[12.5px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] cursor-pointer"
          >
            Reintentar
          </button>
        </div>
      )}

      {!cargando && !error && porConfirmar !== null && (
        <div
          data-testid="cuadro-umbral"
          className="rounded-[var(--radius-sm)] border border-[var(--orange-border)] bg-[var(--orange-bg)] px-3 py-3 text-[12.5px] text-[var(--orange-ink)]"
        >
          <p className="m-0">
            Este periodo y el anterior —que hace falta para comparar— suman{' '}
            <strong>{porConfirmar.toLocaleString('es-ES')} llamadas</strong>. Cargarlas todas puede
            tardar unos segundos.
          </p>
          <button
            type="button"
            onClick={() => setForzar(true)}
            className="mt-2.5 rounded-[var(--radius-sm)] border border-[var(--orange-border)] bg-[var(--surface)] px-3.5 py-1.5 text-[12.5px] font-medium text-[var(--orange-ink)] hover:bg-[var(--surface-2)] cursor-pointer focus-visible:outline-none focus-visible:shadow-[var(--ring)]"
          >
            Cargar de todos modos
          </button>
        </div>
      )}

      {!cargando && !error && porConfirmar === null && (
        <div data-testid="cuadro-contenido">
          {conteoFallido && (
            <p
              data-testid="cuadro-conteo-degradado"
              className="mb-3 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-[12px] text-[var(--text-dim)]"
            >
              No pude comprobar el tamaño del periodo antes de cargarlo. Los datos de abajo son
              correctos; si el periodo es muy largo, puede haber tardado más de lo normal.
            </p>
          )}
          <CabeceraImpresion
            diaDesde={diaDesde}
            diaHasta={diaHasta}
            filtros={filtros}
            razonSocialCliente={razonSocialCliente}
            total={actualF.length}
          />

          <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-2 print:hidden">
            <FiltrosCuadro filtros={filtros} opciones={opciones} onCambiar={setFiltros} />
            <BotonesExportar
              llamadas={actualF}
              periodoVacio={periodoVacio}
              diaDesde={diaDesde}
              diaHasta={diaHasta}
              filtros={filtros}
              razonSocialCliente={razonSocialCliente}
            />
          </div>

          {actualF.length === 0 && actual.length > 0 && (
            <p
              data-testid="cuadro-sin-resultados"
              className="mb-3 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-[12px] text-[var(--text-dim)]"
            >
              Ninguna llamada del periodo cumple estos filtros.
            </p>
          )}

          <KpisCuadro
            kpis={kpisDia(actualF)}
            delta={deltaLlamadas(actualF.length, previoF.length)}
          />

          <div className="grid grid-cols-1 gap-[14px] xl:grid-cols-2 print:grid-cols-2">
            <Panel titulo={TITULO_SERIE[granularidad]}>
              <GraficaBarras serie={serie} granularidad={granularidad} />
            </Panel>
            <Panel titulo="Desenlace">
              <GraficaDonut desenlace={desenlace(actualF)} />
            </Panel>
          </div>

          {/* La columna de clientes, más ancha: las razones sociales son largas y truncarlas esconde justo lo que el bloque quiere enseñar. */}
          <div className="mt-[14px] grid grid-cols-1 gap-[14px] lg:grid-cols-2 xl:grid-cols-[1fr_1.35fr_1fr] print:grid-cols-[1fr_1.35fr_1fr]">
            <Panel titulo="Motivos">
              <DistribucionMotivos
                motivos={distribucionMotivos(actualF)}
                periodoVacio={periodoVacio}
              />
            </Panel>
            <Panel titulo="Clientes que más llaman">
              <ClientesQueMasLlaman
                clientes={clientesQueMasLlaman(actualF)}
                periodoVacio={periodoVacio}
              />
            </Panel>
            <Panel titulo="Derivaciones">
              <Derivaciones derivaciones={derivaciones(actualF)} hayFiltros={hayFiltros} />
            </Panel>
          </div>

          <div className="mt-[14px]">
            <Panel titulo="Últimas llamadas del periodo">
              <TablaLlamadas llamadas={actualF} periodoVacio={periodoVacio} />
            </Panel>
          </div>
        </div>
      )}
    </>
  );
}
